import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { CardStorageService } from 'src/app/common/services/storage/card-storage';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';

import { map, Observable, Subject, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CompetencyDTO } from '../../../dtos/competencies.dto';
import { FileDTO } from '../../../dtos/file.dto';
import { FileType } from '../../../enums/file-type.enum';
import { InstantFeedbackService } from '../../../services/ai/instant-feedback.service';
import { CompetenciesSelectService } from '../../../services/competencies/competencies-select.service';
import { UserPusherService } from '../../../services/pusher/user.pusher.service';
import { SmartFeedbackResponse } from '../../../types/responses/instant-feedback.responses';
import { FileListComponent } from '../../file-management/file-list/file-list.component';
import {
  FileUploadComponent,
  UploadStyles,
} from '../../file-management/file-upload/file-upload.component';

@Component({
  selector: 'app-card-smart-feedback',
  standalone: false,

  templateUrl: './card-smart-feedback.component.html',
  styleUrl: './card-smart-feedback.component.scss',
})
export class CardSmartFeedbackComponent implements OnInit, OnDestroy {
  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @ViewChild('fileListComponent') fileListComponent: FileListComponent;

  subs: Subscription[] = [];

  isCollapsed = this.cardStorageService.getCollapsedState('coacheelist');

  user: User;

  districtIds: number[] = [];

  // CUSTOM COMPETENCY
  competencyList: Observable<CompetencyDTO[]>;

  competencyLibrary: CompetencyDTO[] = [];

  searchInput$ = new Subject<string>();

  selectedCompetency: CompetencyDTO | null = null;

  labelId = uuidv4();

  searchLoading = false;

  // ARTIFACT UPLOAD
  attachments: FileDTO[] = [];

  uploadStyle = UploadStyles.SIMPLE;

  allowedFileTypes = FileType.IMAGE;

  artifact: File | null;

  // NARRATIVE
  narrative: string | null;

  // SUBMIT RESPONSE
  readyToSubmit = false; // var needed to update template state

  submittingData = false;

  smartFeedbackId: number;

  smartFeedback?: SmartFeedbackResponse;

  errorMessage: string;

  constructor(
    private cardStorageService: CardStorageService,
    private store: Store,
    private competenciesSelectService: CompetenciesSelectService,
    private instantFeedbackService: InstantFeedbackService,
    private userPusherService: UserPusherService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit(): void {
    if (
      this.user?.district &&
      !this.user.roles.includes(UserRole.E2L_EMPLOYEE || UserRole.ESUITE_ADMIN)
    ) {
      this.districtIds.push(this.user.district.id);
    }

    this.getCompetencyList();
    this.searchInput$.subscribe((term) => {
      this.searchLoading = true;

      if (this.districtIds.length > 0) {
        return this.competenciesSelectService.search(term, this.districtIds);
      }
      return this.competenciesSelectService.search(term);
    });

    this.subs.push(
      this.userPusherService.feedbackUpdatedNotifier.subscribe((feedback) => {
        if (this.smartFeedbackId === feedback.feedbackId) {
          this.smartFeedback = feedback.smartFeedback;
          this.submittingData = false;
        }
      })
    );
  }

  getCompetencyList() {
    this.competencyList = this.competenciesSelectService.items.pipe(
      map((competencies: CompetencyDTO[]) => {
        this.competencyLibrary = [...this.competencyLibrary, ...competencies];
        this.searchLoading = false;
        return competencies;
      })
    );
  }

  triggerCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.cardStorageService.storeCollapseState(
      'smartFeedback',
      this.isCollapsed
    );
  }

  updateAttachments(file: FileDTO) {
    this.attachments.push(file);
  }

  deleteAttachment(file: FileDTO) {
    this.attachments.forEach((attachment, index) => {
      if (attachment.id === file.id) {
        this.attachments.splice(index, 1);
      }
    });
    this.artifact = null;
    this.checkSubmissonStatus();
  }

  checkSubmissonStatus() {
    if ((this.artifact || this.narrative) && this.selectedCompetency) {
      this.readyToSubmit = true;
    } else {
      this.readyToSubmit = false;
    }
  }

  // Updates local property to be sent in API call on submit
  updateArtifact(file: File) {
    this.artifact = file;
    this.checkSubmissonStatus();
  }

  competencySelected() {
    this.checkSubmissonStatus();
  }

  getSmartFeedback() {
    if (
      this.readyToSubmit &&
      this.selectedCompetency &&
      (this.artifact || this.narrative) &&
      !this.submittingData
    ) {
      this.submittingData = true;
      this.instantFeedbackService
        .getCardFeedback(
          this.selectedCompetency.id,
          this.artifact,
          this.narrative
        )
        .subscribe({
          error: (error) => {
            this.errorMessage = error.error.message;
            this.submittingData = false;
          },
          next: (response) => {
            if ('error' in response) {
              this.errorMessage = response.error;
              this.submittingData = false;
            } else {
              this.smartFeedbackId = response.ai_instant_feedback_id;
            }
          },
        });
    }
  }

  resetCard() {
    this.selectedCompetency = null;
    this.smartFeedback = undefined;
    this.artifact = null;
    this.attachments = [];
    this.narrative = null;
    this.checkSubmissonStatus();
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
